import { styled, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarProps,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import ButtonMobileComponent from 'components/button-mobile'
import { DeleteIcon, EqualizerFilterIcon, ExportIcon, SendIcon, SmsIcon } from 'components/icons'
import useDeviceSize, { DeviceSize } from 'hooks/useDeviceSize'
import * as React from 'react'

const ToolbarContainer = styled(GridToolbarContainer, {
  shouldForwardProp: (prop: any) => prop !== 'isRowsSelected',
})<{ isRowsSelected?: boolean }>(({ theme, isRowsSelected = false }) => ({
  padding: theme.spacing(1, 2),
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: '96px',
  backgroundColor: isRowsSelected ? '#FFFCEA' : '#FFF',
}))

const registerButtonConfig: Record<DeviceSize, { text: string; width: string; padx: string }> = {
  [DeviceSize.xs]: { text: '', width: 'w-[44px]', padx: 'px-[8px]' },
  [DeviceSize.sm]: { text: '', width: 'w-[44px]', padx: 'px-[8px]' },
  [DeviceSize.md]: { text: '', width: 'w-[44px]', padx: 'px-[8px]' },
  [DeviceSize.lg]: { text: 'Register', width: 'w-[100px]', padx: 'px-[8px]' },
  [DeviceSize.xl]: { text: 'Register', width: 'w-[100px]', padx: 'px-[8px]' },
}

const messageButtonConfig: Record<DeviceSize, { text: string; width: string; padx: string }> = {
  [DeviceSize.xs]: { text: '', width: 'w-[44px]', padx: 'px-[8px]' },
  [DeviceSize.sm]: { text: '', width: 'w-[44px]', padx: 'px-[8px]' },
  [DeviceSize.md]: { text: '', width: 'w-[44px]', padx: 'px-[8px]' },
  [DeviceSize.lg]: { text: 'Message', width: 'w-[100px]', padx: 'px-[8px]' },
  [DeviceSize.xl]: { text: 'Message', width: 'w-[100px]', padx: 'px-[8px]' },
}
export const exportButtonConfig: Record<DeviceSize, { text: string; width: string; padx: string }> = {
  [DeviceSize.xs]: { text: '', width: '44px', padx: '8px !important' },
  [DeviceSize.sm]: { text: '', width: '44px', padx: '8px !important' },
  [DeviceSize.md]: { text: '', width: '44px', padx: '8px !important' },
  [DeviceSize.lg]: { text: 'Export', width: '100px', padx: '8px !important' },
  [DeviceSize.xl]: { text: 'Export', width: '100px', padx: '8px !important' },
}
const deleteButtonConfig: Record<DeviceSize, { text: string; width: string; padx: string }> = {
  [DeviceSize.xs]: { text: '', width: 'w-[44px]', padx: 'px-[8px]' },
  [DeviceSize.sm]: { text: '', width: 'w-[44px]', padx: 'px-[8px]' },
  [DeviceSize.md]: { text: '', width: 'w-[44px]', padx: 'px-[8px]' },
  [DeviceSize.lg]: { text: 'Delete', width: 'w-[100px]', padx: 'px-[8px]' },
  [DeviceSize.xl]: { text: 'Delete', width: 'w-[100px]', padx: 'px-[8px]' },
}
interface CustomToolbarProps extends GridToolbarProps {
  rowsSelected?: number
  onRegisterClick?: () => void
  onNotifyClick?: () => void
  onDeleteClick?: () => void
  children?: React.ReactNode
  csvOptions?: any
  printOptions?: any
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({
  rowsSelected,
  onRegisterClick,
  onNotifyClick,
  onDeleteClick,
  children,
  csvOptions,
  printOptions,
}) => {
  const deviceSize: DeviceSize = useDeviceSize()
  return (
    <ToolbarContainer isRowsSelected={!!rowsSelected}>
      {/* Left Side: Search or Selected Row Count */}
      {!!rowsSelected ? (
        <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
          {rowsSelected} {rowsSelected === 1 ? 'family' : 'families'} selected
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GridToolbarQuickFilter
            sx={{
              '& .MuiInputBase-root': {
                height: '44px',
                borderRadius: 2,
                border: '1px solid #D0D5DD',
                backgroundColor: 'background.paper',
                px: 1,
                fontSize: '1rem',
                maxWidth: { xs: 150, md: 'none' },
              },
              '& .MuiInputBase-input': {
                padding: '6px 8px',
              },
              '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                borderBottom: 'none',
              },
            }}
          />
          <GridToolbarFilterButton
            // To make built-in MUI styled button to look like our tailwind styled button
            slotProps={{
              button: {
                startIcon: <EqualizerFilterIcon opacity={!rowsSelected ? 0.5 : 1} />,
                sx: {
                  border: `1px solid #D0D5DD`,
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  width: '44px',
                  minWidth: '44px',
                  height: '44px',
                  textTransform: 'none',
                  paddingX: '8px !important',
                  paddingY: '0px !important',
                  gap: '6px !important',
                  transition: 'none !important', // Disable all transitions!
                  color: 'var(--Headings, #474747)',
                  fontSize: '14px !important', // text-md (14px)
                  fontWeight: '500 !important', // font-medium (500)
                  lineHeight: '1.5 !important', // leading-normal (24px)
                  '& .MuiButton-startIcon': {
                    margin: '0px !important',
                    padding: '0px !important',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                },
              },
            }}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Register Button */}
        {onRegisterClick && (
          <ButtonMobileComponent
            iconPosition={'right'}
            text={registerButtonConfig[deviceSize].text}
            disabled={!rowsSelected}
            data-testid='send-link-button'
            icon={<SendIcon />}
            color='bg-white'
            width={registerButtonConfig[deviceSize].width}
            padx={registerButtonConfig[deviceSize].padx}
            className='border border-[#D0D5DD]'
            onClick={onRegisterClick}
          />
        )}

        {/* Notify Button */}
        {onNotifyClick && (
          <ButtonMobileComponent
            iconPosition={'right'}
            text={messageButtonConfig[deviceSize].text}
            disabled={!rowsSelected}
            data-testid='send-sms-button'
            icon={<SmsIcon />}
            color='bg-white'
            width={messageButtonConfig[deviceSize].width}
            padx={messageButtonConfig[deviceSize].padx}
            className='border border-[#D0D5DD]'
            onClick={onNotifyClick}
          />
        )}

        {/* Export button */}
        <GridToolbarExport
          hideMenu={true}
          csvOptions={csvOptions}
          printOptions={printOptions}
          // To make built-in MUI styled button to look like our tailwind styled button
          slotProps={{
            button: {
              disabled: !rowsSelected,
              startIcon: null,
              endIcon: <ExportIcon opacity={!rowsSelected ? 0.5 : 1} />,
              sx: {
                border: `1px solid ${!rowsSelected ? 'rgba(208,213,221,0.5)' : '#D0D5DD'}`,
                borderRadius: '8px',
                backgroundColor: '#fff',
                width: exportButtonConfig[deviceSize].width,
                minWidth: exportButtonConfig[deviceSize].width,
                height: '44px',
                textTransform: 'none',
                paddingX: exportButtonConfig[deviceSize].padx,
                paddingY: '0px !important',
                gap: '6px !important',
                transition: 'none !important', // Disable all transitions!
                color: 'var(--Headings, #474747)',
                fontSize: '14px !important', // text-md (14px)
                fontWeight: '500 !important', // font-medium (500)
                lineHeight: '1.5 !important', // leading-normal (24px)
                '& .MuiButton-endIcon': {
                  margin: '0px !important',
                  padding: '0px !important',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              },
              // The built-in GridToolbarExport does not support label override via slotProps.button.children.
              // To customize the label, create your own export button and call the export API.
              // This approach gives you full control over the button's appearance and text.
              // children: 'Custom Label',
            },
          }}
        />

        {/* Delete button */}
        {onDeleteClick && (
          <ButtonMobileComponent
            iconPosition={'right'}
            text={deleteButtonConfig[deviceSize].text}
            disabled={!rowsSelected}
            data-testid='bulk-deletion-button'
            icon={<DeleteIcon />}
            color='bg-white'
            width={deleteButtonConfig[deviceSize].width}
            padx={deleteButtonConfig[deviceSize].padx}
            className='border border-[#D0D5DD]'
            onClick={() => {
              onDeleteClick()
            }}
          />
        )}
      </Box>

      {/* Buttons for managing columns, filters, and density */}
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />

      {/* Render children (for NotifyUserComponent) */}
      {children}
    </ToolbarContainer>
  )
}

export default CustomToolbar
